import React, { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { RigidBody, RapierRigidBody } from "@react-three/rapier";
import * as THREE from "three";
import {
  controlsState,
  mouseState,
  yawToForward,
  getOrbitCameraOffset,
  playerPosition,
} from "./controls";
import { startEngine, stopEngine, updateEngine, playBrakeSound, playAccelerateSound } from "./audio";

const MAX_SPEED = 9;
const MAX_REVERSE_SPEED = 4.5;
const ACCEL = 14; // speed units/sec^2 while accelerating
const DECEL = 10; // speed units/sec^2 while coasting/braking
const TURN_RATE = 2.0; // rad/sec for keyboard/touch turning

interface CarProps {
  spawnPosition?: [number, number, number];
  onPositionChange?: (position: THREE.Vector3) => void;
}

const Wheel = React.forwardRef<THREE.Group, { position: [number, number, number] }>(
  ({ position }, ref) => (
    <group ref={ref} position={position}>
      <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.32, 0.32, 0.26, 16]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} />
      </mesh>
    </group>
  ),
);

const Car: React.FC<CarProps> = ({ spawnPosition = [0, 1, 0], onPositionChange }) => {
  const bodyRef = useRef<RapierRigidBody>(null);
  const { camera } = useThree();
  const cameraTarget = useRef(new THREE.Vector3());
  const cameraCurrentPos = useRef(new THREE.Vector3(0, 4, 9));
  const currentSpeed = useRef(0);
  const wheelRotation = useRef(0);
  const wheelRefs = useRef<(THREE.Group | null)[]>([]);
  const wasBraking = useRef(false);
  const wasAccelerating = useRef(false);
  const initialSpawnRef = useRef(spawnPosition);

  useEffect(() => {
    bodyRef.current?.setTranslation(
      { x: spawnPosition[0], y: spawnPosition[1], z: spawnPosition[2] },
      true,
    );
    startEngine();
    return () => stopEngine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, rawDelta) => {
    const body = bodyRef.current;
    if (!body) return;
    const delta = Math.min(rawDelta, 1 / 30);

    // Keyboard/touch A-D also steer the shared orbit yaw (mouse does too)
    const turnInput = (controlsState.left ? 1 : 0) - (controlsState.right ? 1 : 0);
    if (turnInput !== 0) mouseState.orbitYaw += turnInput * TURN_RATE * delta;

    const yaw = mouseState.orbitYaw;
    const forwardVec = yawToForward(yaw);

    let targetSpeed = 0;
    if (controlsState.forward || mouseState.rightDown) targetSpeed = MAX_SPEED;
    else if (controlsState.backward) targetSpeed = -MAX_REVERSE_SPEED;
    if (controlsState.brake) targetSpeed = 0;

    const rate = controlsState.brake
      ? DECEL * 2
      : Math.abs(targetSpeed) > Math.abs(currentSpeed.current)
        ? ACCEL
        : DECEL;
    currentSpeed.current = THREE.MathUtils.damp(currentSpeed.current, targetSpeed, rate, delta);
    if (Math.abs(currentSpeed.current) < 0.01) currentSpeed.current = 0;

    updateEngine(Math.abs(currentSpeed.current) / MAX_SPEED);
    if (controlsState.brake && !wasBraking.current && Math.abs(currentSpeed.current) > 1) {
      playBrakeSound();
    }
    if (
      !wasAccelerating.current &&
      (controlsState.forward || mouseState.rightDown || controlsState.backward) &&
      !controlsState.brake &&
      Math.abs(currentSpeed.current) < 0.5
    ) {
      playAccelerateSound();
    }
    wasBraking.current = controlsState.brake;
    wasAccelerating.current = controlsState.forward || mouseState.rightDown || controlsState.backward;

    const linvel = body.linvel();
    const desired = forwardVec.clone().multiplyScalar(currentSpeed.current);
    body.setLinvel({ x: desired.x, y: linvel.y, z: desired.z }, true);

    // Face the car toward the movement direction (mouse/keyboard controlled)
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    body.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }, true);

    wheelRotation.current += (currentSpeed.current / 0.32) * delta;
    wheelRefs.current.forEach((wheel) => {
      if (wheel) wheel.rotation.x = wheelRotation.current;
    });

    const position = body.translation();
    let pos = new THREE.Vector3(position.x, position.y, position.z);

    // Safety net — teleport back if somehow knocked off the drivable area
    if (pos.y < -5) {
      const [sx, sy, sz] = initialSpawnRef.current;
      const safeY = Math.max(sy, 1.5);
      body.setTranslation({ x: sx, y: safeY, z: sz }, true);
      body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      pos = new THREE.Vector3(sx, safeY, sz);
    }
    onPositionChange?.(pos);
    playerPosition.copy(pos);

    // Orbit camera — follows mouse yaw/pitch around the car
    const offset = getOrbitCameraOffset(yaw, mouseState.orbitPitchT);
    const idealPos = pos.clone().add(offset);
    cameraCurrentPos.current.lerp(idealPos, 1 - Math.pow(0.001, delta));
    camera.position.copy(cameraCurrentPos.current);

    const lookAt = pos.clone();
    lookAt.y += 0.8;
    cameraTarget.current.lerp(lookAt, 1 - Math.pow(0.001, delta));
    camera.lookAt(cameraTarget.current);
  });

  return (
    <RigidBody
      ref={bodyRef}
      colliders="cuboid"
      position={spawnPosition}
      linearDamping={0.5}
      angularDamping={0.5}
      enabledRotations={[false, true, false]}
      mass={1}
    >
      <group>
        {/* Body */}
        <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.2, 0.45, 2.2]} />
          <meshStandardMaterial color="#91bd3a" roughness={0.4} metalness={0.2} />
        </mesh>
        {/* Cabin */}
        <mesh position={[0, 0.52, -0.15]} castShadow>
          <boxGeometry args={[0.9, 0.35, 1.1]} />
          <meshStandardMaterial color="#2d2d2d" roughness={0.3} metalness={0.3} />
        </mesh>
        {/* Wheels */}
        {[
          [-0.68, -0.15, 0.75],
          [0.68, -0.15, 0.75],
          [-0.68, -0.15, -0.75],
          [0.68, -0.15, -0.75],
        ].map((pos, i) => (
          <Wheel
            key={i}
            position={pos as [number, number, number]}
            ref={(el) => {
              wheelRefs.current[i] = el;
            }}
          />
        ))}
      </group>
    </RigidBody>
  );
};

export default Car;
